import React, { useEffect, useState } from 'react';
import propTypes from 'prop-types';
import moment from 'moment-timezone';
import {
  Alert,
  Button,
  Row,
  Col,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from 'reactstrap';
import ModalMarketplacePickup from '../Sales/PreDelivering/Components/ModalMarketplacePickup';
import { ShippingCard } from '../Sales/Delivering/Modals';
import { TextRupiah } from '../../components/Text';
import InputDatetime from '../../components/Form/InputDatetime/InputDatetime';
import { dateChange, dateValue } from '../../utils/form';
import ReturnQcForm from './ReturnQcFields';
import { returnQcPayload } from './returnQcForm';
import ReturnRelistFields from './ReturnRelistFields';
import { returnRelistPayload } from './returnRelistForm';
import ReturnRelistAccount from './ReturnRelistAccount';
import { rackFitsQuantity } from '../../helpers/rackCapacity';
import {
  RETURN_SHIPMENT_COURIERS,
  isPositiveAskingPrice,
  readSaleReturn,
  returnErrorMessage,
} from './returnSale';

const confirmCopy = {
  buyer_approve: 'Return Approved will move this return to Need Relisting and Need Return Refund.',
  buyer_reject: 'Sending Back To Buyer will move this return to Pre Delivering.',
  pass: 'Pass Quality Control and move this return to relisting.',
  refund: 'Record the refund and move this return to processed.',
  dispatch: 'Hand this return to the courier and move it to delivering.',
  reject: 'Complete this rejected return.',
};

const modalTitle = {
  buyer_approve: 'Return Approved',
  buyer_reject: 'Sending Back To Buyer',
  awb: 'Buyer airway bill',
  receipt: 'Receive Sneakers Recap',
  pass: 'Pass Quality Control',
  fail: 'Fail Quality Control',
  relist: 'Relist',
  refund: 'Confirmation',
  shipment: 'Delivery Order',
  dispatch: 'Hand to courier',
  reject: 'Complete rejected return',
};

const ReturnActionModal = ({
  isOpen,
  kind,
  sale,
  isSubmit,
  onCancel,
  onSubmit,
  onSearchUsers,
  receivedBy,
}) => {
  const [awb, setAwb] = useState('');
  const [reason, setReason] = useState('');
  const [courier, setCourier] = useState('jnt');
  const [receivedAt, setReceivedAt] = useState(() => moment.tz('Asia/Jakarta').format('YYYY-MM-DD HH:mm:00'));
  const [selectedUser, setSelectedUser] = useState(null);
  const [error, setError] = useState(null);
  const [inspectionForm, setInspectionForm] = useState(null);
  const [relistForm, setRelistForm] = useState(null);
  const saleId = sale ? sale.id : null;
  const { data: returnData } = readSaleReturn(sale || {});
  const shipmentAwb = (returnData.shipment || {}).awb_number || '';
  const shipmentCourier = (returnData.shipment || {}).courier || 'jnt';
  const buyerAwb = (returnData.buyer_shipment && returnData.buyer_shipment.awb_number) || '';

  useEffect(() => {
    if (!isOpen) return;
    setError(null);
    setAwb(kind === 'receipt' ? buyerAwb : kind === 'shipment' ? shipmentAwb : '');
    setReason('');
    setCourier(kind === 'shipment' ? shipmentCourier : 'jnt');
    setReceivedAt(moment.tz('Asia/Jakarta').format('YYYY-MM-DD HH:mm:00'));
    setSelectedUser(null);
    setInspectionForm(null);
    setRelistForm(null);
  }, [isOpen, saleId, kind, buyerAwb, shipmentAwb, shipmentCourier]);

  const isDecision = kind === 'buyer_approve' || kind === 'buyer_reject';
  const dismiss = isSubmit ? undefined : onCancel;

  const handleSubmit = (event) => {
    event.preventDefault();
    if (isSubmit || !sale || kind === 'pickup') return;

    if (kind === 'awb' || kind === 'shipment' || kind === 'receipt') {
      if (awb.trim() === '') {
        setError('The airway bill is required.');
        return;
      }
    }
    if (kind === 'fail' && reason.trim() === '') {
      setError('A follow-up reason is required.');
      return;
    }
    if (kind === 'receipt') {
      if (!receivedAt || !moment(receivedAt, 'YYYY-MM-DD HH:mm:ss', true).isValid()) {
        setError('Receipt time is not a valid date.');
        return;
      }
    }
    if (kind === 'pass' && (!inspectionForm || !inspectionForm.product || !inspectionForm.size || !inspectionForm.sneakers_condition || !inspectionForm.box_condition)) {
      setError('Choose the product variant, size, listing condition and box condition.');
      return;
    }
    if (kind === 'relist') {
      if (!relistForm || !relistForm.product || !relistForm.size || !relistForm.sneakers_condition || !relistForm.box_condition) {
        setError('Choose the product variant, size, listing condition and box condition.');
        return;
      }
      if (selectedUser == null) {
        setError('Choose the Kick Avenue account that will own the new listing.');
        return;
      }
      if (!/^\d+$/.test(relistForm.quantity) || !Number.isSafeInteger(Number(relistForm.quantity)) || Number(relistForm.quantity) < 1) {
        setError('Qty must be a whole number greater than zero.');
        return;
      }
      if (!isPositiveAskingPrice(relistForm.asking_price) || relistForm.asking_price_loading) {
        setError('Asking price must be a whole rupiah amount greater than zero.');
        return;
      }
      if (!relistForm.rack || !relistForm.rack.id) {
        setError('Choose a rack for the relisted item.');
        return;
      }
      if (!rackFitsQuantity(relistForm.rack.count, relistForm.rack.max, relistForm.quantity)) {
        setError('The selected rack does not have enough capacity. Choose another rack.');
        return;
      }
      if (relistForm.purchase_price.trim() !== '' && !/^\d+(\.\d{1,2})?$/.test(relistForm.purchase_price.trim())) {
        setError('Purchase price must be a non-negative amount with at most two decimals.');
        return;
      }
    }

    let body = {};
    if (kind === 'awb') body = { awb_number: awb.trim() };
    if (kind === 'receipt') body = { received_at: receivedAt, awb_number: awb.trim() };
    if (kind === 'pass') body = { stage: 'qc', passed: true, form1: returnQcPayload(inspectionForm) };
    if (kind === 'fail') body = { stage: 'qc', passed: false, reason: reason.trim() };
    if (kind === 'shipment') body = { courier, awb_number: awb.trim() };
    if (kind === 'buyer_approve') body = { decision: 'approve' };
    if (kind === 'buyer_reject') body = { decision: 'reject' };
    if (kind === 'relist') {
      body = returnRelistPayload(relistForm, selectedUser.id);
    }

    setError(null);
    Promise.resolve(onSubmit(sale.id, kind, body)).then((result) => {
      if (!result || !result.ok) {
        setError(returnErrorMessage(result && result.error));
      }
    });
  };

  if (kind === 'pickup') {
    const shipment = returnData.shipment || {};
    const pickupSale = sale ? {
      ...sale,
      sale_shipping: {
        ...(sale.sale_shipping || {}),
        awb_number: shipment.awb_number || '',
        pickup_code: shipment.pickup_code || '',
      },
    } : null;
    return <ModalMarketplacePickup isOpen={isOpen} sale={pickupSale} onCancel={onCancel}
      onConfirm={() => onSubmit(sale.id, 'pickup', {})} />;
  }

  return (
    <Modal isOpen={isOpen} toggle={dismiss} size={kind === 'relist' || kind === 'pass' ? 'lg' : undefined}>
      <form onSubmit={handleSubmit}>
        <ModalHeader toggle={dismiss}>{modalTitle[kind] || 'Return'}</ModalHeader>
        <ModalBody>
          {error ? <Alert color="danger">{error}</Alert> : null}
          {kind === 'refund' && sale ? <p>
            Invoice: <i>({sale.invoice_number})</i><br />
            Email: <i>({sale.buyer_email || '-'})</i><br />
            Total: <TextRupiah tag="span" prefix="Rp. " value={sale.total_price == null ? 0 : sale.total_price} bold />
          </p> : null}
          {confirmCopy[kind] ? <p className="mb-0">{confirmCopy[kind]}</p> : null}
          {kind === 'pass' && isOpen && sale ? (
            <ReturnQcForm saleId={sale.id} value={inspectionForm} onChange={setInspectionForm} disabled={isSubmit} />
          ) : null}
          {kind === 'awb' ? (
            <FormGroup>
              <Label for="return-awb">Airway bill</Label>
              <Input
                id="return-awb"
                value={awb}
                onChange={(event) => setAwb(event.target.value)}
                disabled={isSubmit}
              />
            </FormGroup>
          ) : null}
          {kind === 'shipment' && sale ? <>
            <Row><Col sm={12}><ShippingCard displayName={sale.sale_product && sale.sale_product.display_name}
              invoiceNumber={sale.invoice_number} shipping={sale.sale_shipping || {}} /></Col></Row>
            <Row>
              <Col xs={6}><FormGroup>
                <Label for="return-courier">Courier*</Label>
                <Input id="return-courier" type="select" value={courier} onChange={(event) => setCourier(event.target.value)} disabled={isSubmit}>
                  {RETURN_SHIPMENT_COURIERS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                </Input>
              </FormGroup></Col>
              <Col xs={6}><FormGroup>
                <Label for="return-awb">AWB Number*</Label>
                <Input id="return-awb" value={awb} onChange={(event) => setAwb(event.target.value)} disabled={isSubmit} />
              </FormGroup></Col>
            </Row>
            <span className="text-sm">Fields marked with * are required</span>
          </> : null}
          {kind === 'receipt' ? (
            <>
              <p>
                <b>({sale && sale.invoice_number})</b><br />
                <i>{sale && sale.sale_product && sale.sale_product.display_name}</i>
              </p>
              <FormGroup>
                <Label for="return-received-by">Receiver Name</Label>
                <Input id="return-received-by" value={receivedBy || ''} readOnly />
              </FormGroup>
              <FormGroup>
                <Label for="return-buyer-awb">Awb Number</Label>
                <Input
                  id="return-buyer-awb"
                  value={awb}
                  onChange={(event) => setAwb(event.target.value)}
                  maxLength={80}
                  disabled={isSubmit}
                />
              </FormGroup>
              <FormGroup>
                <Label for="return-received-at">Received At*</Label>
                <InputDatetime
                  value={dateValue('received_at', { received_at: receivedAt })}
                  inputProps={{ id: 'return-received-at', placeholder: 'When departed in kickavenue?*', disabled: isSubmit }}
                  onChange={(value) => setReceivedAt(dateChange(value, 'received_at', {}).received_at)}
                  timeFormat="HH:mm"
                />
              </FormGroup>
              <span className="text-sm">Fields marked with * are required</span>
            </>
          ) : null}
          {kind === 'fail' ? (
            <FormGroup>
              <Label for="return-reason">Reason</Label>
              <Input
                id="return-reason"
                type="textarea"
                value={reason}
                onChange={(event) => setReason(event.target.value)}
                disabled={isSubmit}
              />
            </FormGroup>
          ) : null}
          {kind === 'relist' && isOpen && sale ? (
            <ReturnRelistFields
              saleId={sale.id}
              value={relistForm}
              onChange={setRelistForm}
              disabled={isSubmit}
              accountField={<ReturnRelistAccount sale={sale} onSelect={setSelectedUser} onSearchUsers={onSearchUsers} disabled={isSubmit} />}
            />
          ) : null}
        </ModalBody>
        <ModalFooter>
          <Button type="button" color="secondary" onClick={onCancel} disabled={isSubmit}>
            Cancel
          </Button>
          <Button type="submit" color="primary" disabled={isSubmit || (kind === 'pass' && !inspectionForm) || (kind === 'relist' && (!relistForm || relistForm.asking_price_loading || !isPositiveAskingPrice(relistForm.asking_price) || !selectedUser || !relistForm.rack || !relistForm.rack.id))}>
            {isSubmit ? 'Saving...' : kind === 'receipt' ? 'Receive' : kind === 'refund' || isDecision ? 'Confirm' : kind === 'shipment' ? 'Submit' : 'Save'}
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
};

ReturnActionModal.propTypes = {
  isOpen: propTypes.bool,
  kind: propTypes.string,
  sale: propTypes.object,
  isSubmit: propTypes.bool,
  onCancel: propTypes.func.isRequired,
  onSubmit: propTypes.func.isRequired,
  onSearchUsers: propTypes.func,
  receivedBy: propTypes.string,
};

ReturnActionModal.defaultProps = {
  isOpen: false,
  kind: null,
  sale: null,
  isSubmit: false,
  onSearchUsers: () => Promise.resolve({ ok: true, data: { data: [] } }),
};

export default ReturnActionModal;
